import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { naira } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, remove, subtotal, count } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-bone border-l border-sandline flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-sandline text-left">
          <SheetTitle className="font-serif text-2xl text-espresso">
            Your Bag {count > 0 && <span className="text-bark text-base">({count})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
            <ShoppingBag className="w-12 h-12 text-sandline" strokeWidth={1} />
            <p className="text-bark">Your bag is empty.</p>
            <Button
              onClick={() => setOpen(false)}
              className="rounded-full bg-espresso text-bone hover:bg-terracotta"
              data-testid="cart-continue-shopping"
              asChild
            >
              <Link to="/shop">Start shopping</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover rounded-xl bg-sand" />
                  <div className="flex-1">
                    <h4 className="font-serif text-lg text-espresso leading-tight">{item.name}</h4>
                    <p className="text-bark text-sm mb-2">{naira(item.price)}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-sandline rounded-full">
                        <button
                          onClick={() => updateQty(item.id, item.quantity - 1)}
                          className="p-1.5 hover:text-terracotta"
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.id, item.quantity + 1)}
                          className="p-1.5 hover:text-terracotta"
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => remove(item.id)}
                        className="text-bark hover:text-destructive"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <span className="font-semibold text-espresso">{naira(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>
            <SheetFooter className="px-6 py-5 border-t border-sandline block">
              <div className="flex justify-between mb-4">
                <span className="text-bark">Subtotal</span>
                <span className="font-serif text-2xl text-espresso" data-testid="cart-subtotal">
                  {naira(subtotal)}
                </span>
              </div>
              <Button
                asChild
                onClick={() => setOpen(false)}
                className="w-full rounded-full bg-terracotta text-bone hover:bg-espresso h-12 text-base"
                data-testid="cart-checkout-button"
              >
                <Link to="/checkout">Checkout</Link>
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
